"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.contextHasLegacyPlanEpoch = contextHasLegacyPlanEpoch;
exports.contextHasNewPlanEpoch = contextHasNewPlanEpoch;
exports.contextHasReverseTrialType = contextHasReverseTrialType;
exports.contextHasPaymentDetailsUpFrontTrialType = contextHasPaymentDetailsUpFrontTrialType;
exports.getLogInformationForContext = getLogInformationForContext;
exports.retrieveContextForUserUid = retrieveContextForUserUid;
exports.retrieveContextForUser = retrieveContextForUser;
exports.retrieveContextForOrganization = retrieveContextForOrganization;
exports.getUserContextFromParts = getUserContextFromParts;
exports.getOrganizationContextFromParts = getOrganizationContextFromParts;
exports.getEnterpriseContextFromParts = getEnterpriseContextFromParts;
const models_1 = require("@buildwithflux/models");
function contextHasLegacyPlanEpoch(context) {
    return (!context.currentUserPrivateMetadata.creationFlags?.planEpoch ||
        context.currentUserPrivateMetadata.creationFlags?.planEpoch === models_1.PlanEpoch.enum.legacy);
}
function contextHasNewPlanEpoch(context) {
    return context.currentUserPrivateMetadata.creationFlags?.planEpoch === models_1.PlanEpoch.enum.from_202410;
}
/**
 * Whether the entitlement context contains a flag indicating a regular reverse trial is to be given
 *
 * @todo Can be simplified after backfill from FLUX-4579 is complete
 */
function contextHasReverseTrialType(context) {
    return (contextHasNewPlanEpoch(context) &&
        (!context.currentUserPrivateMetadata.creationFlags?.trialType ||
            context.currentUserPrivateMetadata.creationFlags?.trialType === models_1.TrialType.enum.reverse));
}
/**
 * Whether the entitlement context contains a flag indicating a payment-details-up-front trial is to be given, only
 * after the user provides those payment details to Stripe
 */
function contextHasPaymentDetailsUpFrontTrialType(context) {
    return (contextHasNewPlanEpoch(context) &&
        context.currentUserPrivateMetadata.creationFlags?.trialType === models_1.TrialType.enum.payment_details_up_front);
}
function getLogInformationForContext(context) {
    const details = {
        contextType: context.type,
        currentUserUid: context.currentUser.uid,
        stripeClientMode: context.stripeClientMode,
    };
    if (context.type === "organization") {
        details.organizationUid = context.membership.organizationUid;
    }
    if (context.type === "enterprise") {
        details.enterpriseUid = context.membership.enterpriseUid;
        details.defaultOrganizationUid = context.membership.enterprise.defaultOrganizationUid;
    }
    return details;
}
/**
 * Function for producing a context from just a user UID
 *
 * Note that this function should either be used by the backend, or the currentUserUid should belong to the current
 * user (because you won't have permission to access te user's private metadata otherwise)
 */
async function retrieveContextForUserUid(currentUserUid, userRepository, userPrivateMetadataRepository, stripeClientMode) {
    const currentUserPrivateMetadata = userPrivateMetadataRepository.getForUser(currentUserUid);
    const currentUser = userRepository.getByUid(currentUserUid);
    return getUserContextFromParts((await currentUser) ?? { uid: currentUserUid, activeProductUid: undefined, created_at: Date.now() }, (await currentUserPrivateMetadata) ?? { uid: currentUserUid }, stripeClientMode);
}
/**
 * Function for producing a context from a user object that is already retrieved
 *
 * Note that this function should either be used by the backend, or the user should be the current user (because you
 * won't have permission to access the user's private metadata otherwise)
 */
async function retrieveContextForUser(currentUser, userPrivateMetadataRepository, stripeClientMode) {
    const currentUserPrivateMetadata = userPrivateMetadataRepository.getForUser(currentUser.uid);
    return getUserContextFromParts(currentUser, (await currentUserPrivateMetadata) ?? { uid: currentUser.uid }, stripeClientMode);
}
/**
 * Function for producing a context from a user and optionally an organization too
 *
 * This function is a bit strange: it's for cases where we want to look up entitlements of an organization independently
 * of which user within the organization is currently logged in. This is a bit of a niche use-case, mainly useful from
 * the backend: in most other cases, you'd have a specific current user as well as the organization they're operating
 * within.
 *
 * Most frontend application should already be using the hook versions of these functions: e.g. useEntitlementContext()
 *
 * Note that this function should either be used by the backend, or the account should be the current user, or an
 * organization the current user is a member of (because you won't have permission to access the account's private
 * metadata otherwise)
 */
async function retrieveContextForOrganization(organization, currentUser, userPrivateMetadataRepository, organizationRepository, enterpriseRepository, stripeClientMode) {
    const membership = await organizationRepository.getOrganizationMember(organization.uid, currentUser.uid);
    if (!membership) {
        throw new Error(`No membership found for user ${currentUser.uid} within organization ${organization.uid}`);
    }
    const userContext = retrieveContextForUser(currentUser, userPrivateMetadataRepository, stripeClientMode);
    const decoratedMembership = await enterpriseRepository.fullyDecorateOrganizationMember(membership);
    if ((0, models_1.isEnterpriseFullyDecoratedOrganizationMember)(decoratedMembership)) {
        return getEnterpriseContextFromParts(await userContext, decoratedMembership);
    }
    else {
        return getOrganizationContextFromParts(await userContext, decoratedMembership);
    }
}
/**
 * Function for producing a user context from its constituent parts
 */
function getUserContextFromParts(currentUser, currentUserPrivateMetadata, stripeClientMode) {
    return {
        type: "user",
        currentUser,
        currentUserPrivateMetadata,
        stripeClientMode,
    };
}
/**
 * Function for producing an organization context from its constituent parts
 */
function getOrganizationContextFromParts(userContext, membership) {
    return {
        ...userContext,
        type: "organization",
        membership,
    };
}
/**
 * Function for producing an enterprise context from its constituent parts
 */
function getEnterpriseContextFromParts(userContext, membership) {
    return {
        ...userContext,
        type: "enterprise",
        membership,
    };
}
//# sourceMappingURL=context.js.map